import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { dmcaService, type DmcaTargetsParams } from "@/services/DmcaService";
import type { DmcaSettings } from "@/types/dmca";

// Health (last_check_at / checks_24h / errors_24h) must age without a reload, so
// the summary refetches on an interval — same reasoning as the Clicks freshness
// badges. Fail-soft everywhere: a dead RPC leaves the page usable, not thrown.
export const useDmcaSummary = () =>
  useQuery({
    queryKey: ["dmca", "summary"] as const,
    queryFn: () => dmcaService.getSummary(),
    staleTime: 1000 * 60 * 2,
    refetchInterval: 1000 * 60 * 5,
    throwOnError: false,
  });

export const useDmcaTargets = (params: DmcaTargetsParams) =>
  useQuery({
    queryKey: ["dmca", "targets", params] as const,
    queryFn: () => dmcaService.getTargets(params),
    placeholderData: keepPreviousData, // smooth paging/filtering
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

// URL children of a domain — fetched only when the domain row is expanded, so
// opening N rows is N lazy queries, not N eager ones.
export const useDmcaUrlChildren = (domain: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["dmca", "url-children", domain] as const,
    queryFn: () => dmcaService.getUrlChildren(domain as string, false),
    enabled: enabled && Boolean(domain),
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

// Tiering + schedule settings (dmca_settings row).
export const useDmcaSettings = () =>
  useQuery({
    queryKey: ["dmca", "settings"] as const,
    queryFn: () => dmcaService.getSettings(),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });

export const useUpdateDmcaSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<DmcaSettings>) => dmcaService.updateSettings(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dmca", "settings"] }),
  });
};

// ---- VIP overrides ----------------------------------------------------------

export const useDmcaOverrides = () =>
  useQuery({
    queryKey: ["dmca", "overrides"] as const,
    queryFn: () => dmcaService.getOverrides(),
    staleTime: 1000 * 60 * 2,
    throwOnError: false,
  });

// Target search for the add box — debounced by the caller via `query`.
export const useTargetSearch = (query: string) =>
  useQuery({
    queryKey: ["dmca", "target-search", query] as const,
    queryFn: () => dmcaService.searchTargets(query),
    enabled: query.trim().length > 0,
    staleTime: 1000 * 30,
    throwOnError: false,
  });

const invalidateOverrides = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["dmca", "overrides"] });

export const useAddOverride = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { targetId: number; intervalHours: number; reason: string | null }) =>
      dmcaService.addOverride(v.targetId, v.intervalHours, v.reason),
    onSuccess: () => invalidateOverrides(qc),
  });
};

export const useUpdateOverrideInterval = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; intervalHours: number }) =>
      dmcaService.updateOverrideInterval(v.id, v.intervalHours),
    onSuccess: () => invalidateOverrides(qc),
  });
};

export const useRemoveOverride = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => dmcaService.removeOverride(id),
    onSuccess: () => invalidateOverrides(qc),
  });
};
